import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Feature kinds the super-admin can globally enable / disable. Drives whether the
 * feature shows up at all on the public pricing page, in the tenant nav, in the
 * Profile sliders, in /docs, and in the agency landing/registration/portal surfaces.
 * Mirrors `FeatureKind` on the API side.
 */
export type FeatureKind =
  | 'blacklist'
  | 'inbox_placement'
  | 'phone_validation'
  | 'warmup_inbox'
  | 'domain_health'
  | 'agency'
  | 'affiliate';

export const ALL_FEATURE_KINDS: ReadonlyArray<FeatureKind> = [
  'blacklist',
  'inbox_placement',
  'phone_validation',
  'warmup_inbox',
  'domain_health',
  'agency',
  'affiliate',
];

const FEATURE_LABEL: Record<FeatureKind, string> = {
  blacklist: 'Blacklist Checks',
  inbox_placement: 'Inbox Placement',
  phone_validation: 'Phone Validation',
  warmup_inbox: 'Warmup Inbox',
  domain_health: 'Domain Health',
  agency: 'Agency mode',
  affiliate: 'Affiliate / Partner program',
};

export function featureLabel(kind: FeatureKind): string {
  return FEATURE_LABEL[kind];
}

/**
 * Fetch the live feature-availability map from /platform/features. Public, unauth'd
 * — same endpoint serves the marketing pricing page + the in-app tenant portal +
 * the admin settings UI. Missing keys default to enabled (matches the API contract).
 *
 * `feature(kind)` returns true on loading (optimistic) so the page can render without
 * a flash of "feature missing"; once the response lands the gating kicks in. Use
 * `feature.isLoading` if you need to defer rendering until the truth lands.
 */
export function useFeatures() {
  const query = useQuery({
    queryKey: ['platform-features'],
    queryFn: () => api<Record<FeatureKind, boolean>>('/platform/features', { auth: false }),
    staleTime: 5 * 60_000,
  });

  const flags = query.data;
  const feature = (kind: FeatureKind): boolean => flags?.[kind] ?? true;
  return {
    isLoading: query.isLoading,
    error: query.error,
    flags,
    feature,
  };
}
